import { useDeleteNotification } from "@/mutations/useDeleteNotification.tsx";
import { useFetchNotificationsForUser } from "@/queries/useFetchNotificationsForUser.ts";
import { getErrorMessage } from "@/utils/api.ts";
import { Box, Divider, Link, Menu, MenuItem, Typography, useTheme } from "@mui/material";
import { type PopupState, bindMenu } from "material-ui-popup-state/hooks";
import { enqueueSnackbar } from "notistack";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

type Props = {
    state: PopupState;
};

const NotificationMenu = ({ state }: Props): ReactNode => {
    const theme = useTheme();
    const deleteNotification = useDeleteNotification();

    const { data: notifications = [] } = useFetchNotificationsForUser();

    // Filter to show only unread notifications in the bell menu
    const unreadNotifications = notifications.filter((n) => n.hasUnreadNotification);
    const unreadCount = unreadNotifications.length;

    const handleClearNotification = (notificationUUID: string, noteId?: string) => {
        deleteNotification.mutate(
            {
                notificationUUID,
                noteId,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Notification cleared.", { variant: "success" });
                },
                onError: (error) => {
                    enqueueSnackbar("Error: Notification could not be removed.", {
                        variant: "error",
                    });
                    console.info("error", getErrorMessage(error));
                },
            },
        );
    };

    const getPriorityColor = (priority: string | null) => {
        switch (priority) {
            case "high":
                return "#f44336";
            case "medium":
                return "#ff9800";
            case "low":
                return "#4caf50";
            default:
                return "#4caf50";
        }
    };

    return (
        <Menu
            {...bindMenu(state)}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 350,
                        maxHeight: 400,
                        marginTop: theme.spacing(1),
                    },
                },
            }}
        >
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 500 }}>
                    Notifications ({unreadCount} unread)
                </Typography>
            </Box>

            {unreadNotifications.length === 0 ? (
                <MenuItem>
                    <Typography variant="body2" color="text.secondary">
                        No unread notifications
                    </Typography>
                </MenuItem>
            ) : (
                unreadNotifications.map((notification) => (
                    <MenuItem
                        key={notification.id}
                        onClick={() =>
                            handleClearNotification(
                                notification.id,
                                notification.note?.idUUID ?? undefined,
                            )
                        }
                        sx={{
                            padding: theme.spacing(2),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            alignItems: "flex-start",
                            backgroundColor: notification.hasUnreadNotification
                                ? "transparent"
                                : "action.hover",
                            opacity: notification.hasUnreadNotification ? 0.7 : 1,
                            cursor: notification.hasUnreadNotification ? "default" : "pointer",
                            "&:last-child": {
                                borderBottom: "none",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                marginTop: theme.spacing(0.5),
                                marginRight: theme.spacing(1),
                                backgroundColor: getPriorityColor(notification.priority),
                            }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: "0.875rem",
                                    fontWeight: 500,
                                    marginBottom: theme.spacing(0.5),
                                }}
                            >
                                {notification.title}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Case #
                                <Link
                                    component={RouterLink}
                                    to={`/case-detail/${notification.case.id}`}
                                    sx={{
                                        color: "primary.main",
                                        textDecoration: "none",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {notification.case.id}
                                </Link>{" "}
                                - {notification.case.clientName}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                {notification.updated}
                            </Typography>
                            {notification.hasUnreadNotification && (
                                <Typography
                                    sx={{
                                        fontSize: "0.75rem",
                                        color: "primary.main",
                                        fontWeight: 400,
                                        fontStyle: "italic",
                                        mt: 0.5,
                                    }}
                                >
                                    (Click to mark as read)
                                </Typography>
                            )}
                        </Box>
                    </MenuItem>
                ))
            )}

            <Divider />
            {unreadNotifications.length > 0 && (
                <MenuItem onClick={() => handleClearNotification("all")}>
                    <Typography
                        variant="body2"
                        color="primary"
                        sx={{ textAlign: "center", width: "100%" }}
                    >
                        Mark All as Read
                    </Typography>
                </MenuItem>
            )}
        </Menu>
    );
};

export default NotificationMenu;
